/**
 * useAppFeatures - DB-driven feature and group data
 *
 * Replaces hardcoded ALL_MODULES and MODULE_GROUPS by fetching from
 * app_views and nav_groups tables. Single source of truth for all
 * feature/module lists in the admin UI.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { apiRequest } from '../../api/core.js';

// Icon name fallback mapping (view_code -> lucide icon name)
const DEFAULT_ICONS = {
  dashboard: 'LayoutDashboard',
  vehicles: 'Car',
  drivers: 'Users',
  maintenance: 'Wrench',
  fuel: 'Fuel',
  inspections: 'ClipboardCheck',
  insurance: 'Shield',
  claims: 'AlertTriangle',
  documents: 'FileText',
  compliance: 'ShieldCheck',
  costs: 'CreditCard',
  reports: 'BarChart3',
  telematics: 'MapPin',
  tools: 'Hammer',
  analytics: 'TrendingUp',
  alerts: 'Bell',
  pipeline: 'ListChecks',
  sites: 'Building2',
  fines: 'Receipt',
  suppliers: 'Truck',
  assignments: 'UserCheck',
  events: 'Calendar',
  cockpit: 'Gauge',
  shares: 'Share2',
  'risk-profiles': 'ShieldAlert',
  emails: 'Mail',
  'report-archive': 'Archive',
  campaigns: 'Megaphone',
  radar: 'Radar',
};

export function useAppFeatures() {
  const [features, setFeatures] = useState([]);
  const [groups, setGroups] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const cacheRef = useRef(null);

  const load = useCallback(async (force = false) => {
    if (cacheRef.current && !force) {
      setFeatures(cacheRef.current.features);
      setGroups(cacheRef.current.groups);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const [viewsRes, groupsRes] = await Promise.all([
        apiRequest('/app_views?_limit=200'),
        apiRequest('/nav_groups?_limit=50'),
      ]);

      // Build groups map keyed by group_code
      const groupsMap = {};
      if (groupsRes.ok && groupsRes.data) {
        groupsRes.data
          .sort((a, b) => (a.group_order || 0) - (b.group_order || 0))
          .forEach(g => {
            groupsMap[g.group_code] = {
              code: g.group_code,
              label: g.group_name || g.group_code,
              icon: g.icon || null,
              order: g.group_order || 99,
            };
          });
      }

      // Build features list from app_views
      const featuresList = [];
      if (viewsRes.ok && viewsRes.data) {
        viewsRes.data.forEach(v => {
          featuresList.push({
            id: v.view_code,
            label: v.view_name || v.view_code,
            icon: v.icon || DEFAULT_ICONS[v.view_code] || 'Box',
            group: v.nav_group || 'other',
            adminOnly: !!v.admin_only,
            superuserOnly: !!v.superuser_only,
            isEnabled: !!v.is_enabled,
            componentName: v.component_name || null,
            order: v.nav_order || 99,
          });
        });
      }

      featuresList.sort((a, b) => a.order - b.order);

      cacheRef.current = { features: featuresList, groups: groupsMap };
      setFeatures(featuresList);
      setGroups(groupsMap);
    } catch (err) {
      console.error('[useAppFeatures] Failed to load:', err);
      setError(err.message || 'Failed to load features');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Group features by nav_group
  const featuresByGroup = {};
  features.forEach(f => {
    const key = f.group || 'other';
    if (!featuresByGroup[key]) featuresByGroup[key] = [];
    featuresByGroup[key].push(f);
  });

  return {
    features,
    groups,
    featuresByGroup,
    isLoading,
    error,
    refresh: () => load(true),
  };
}

export default useAppFeatures;
